import React, { useState, useEffect } from 'react';
import AnimatedCounter from './AnimatedCounter';
import { useSpring, animated as a } from 'react-spring';

interface MetricCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  subValue?: number;
  change?: number;
  format?: 'number' | 'currency' | 'percentage' | 'string';
  description?: string;
  variant?: 'primary' | 'success' | 'warning' | 'danger' | 'purple';
  icon?: React.ReactNode;
  animated?: boolean;
  pulse?: boolean;
  className?: string;
  onClick?: () => void;
}

function MetricCard({
  title,
  value,
  subtitle,
  subValue,
  change,
  format = 'number',
  description,
  variant = 'primary',
  icon,
  animated = false,
  pulse = false,
  className = '',
  onClick
}: MetricCardProps) {
  const [hovered, setHovered] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  
  // Format the value based on the format prop
  const formattedValue = (): string => {
    if (typeof value === 'string') return value;

    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);

      case 'percentage':
        return `${value.toFixed(1)}%`;

      case 'string':
        return String(value);

      case 'number':
      default:
        return new Intl.NumberFormat('pt-BR').format(value);
    }
  };

  // Formatter functions for AnimatedCounter
  const formatters = {
    currency: (val: number) => new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val),
    percentage: (val: number) => `${val.toFixed(1)}%`,
    string: (val: number) => String(val),
    number: (val: number) => new Intl.NumberFormat('pt-BR').format(val),
  };

  // Determine color based on change value
  const getChangeColor = () => {
    if (!change) return '';
    return change > 0 ? 'positive-change' : change < 0 ? 'negative-change' : '';
  };

  // Format the change percentage
  const formattedChange = change
    ? `${change > 0 ? '+' : ''}${change.toFixed(1)}%`
    : null;
    
  // Determinar se este é o card "Total de Hoje"
  const isTotalHojeCard = title === "Total de Hoje";
  
  // Verificar se o valor é muito grande
  const isLargeValue = typeof value === 'number' && value >= 1000;
  
  // Animações com react-spring para entrada
  const fadeInAnimation = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
    delay: Math.random() * 300, // Delay aleatório para efeito escalonado
    config: { tension: 280, friction: 20 }
  });
  
  // Animação de hover
  const hoverAnimation = useSpring({
    transform: hovered ? 'translateY(-5px)' : 'translateY(0px)',
    boxShadow: hovered 
      ? '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)'
      : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    config: { tension: 300, friction: 20 }
  });

  // Efeito para ativar animação de destaque quando o valor muda
  useEffect(() => {
    setHasBeenVisible(true);
  }, []);

  // Detectar se o componente está em tela
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    const currentRef = document.querySelector(`.metric-card.${variant}`);
    if (currentRef) observer.observe(currentRef);
    
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [variant]);

  return (
    <a.div 
      className={`metric-card ${variant} ${pulse ? 'pulse-animation' : ''} ${className} ${isLargeValue ? 'has-large-value' : ''}`}
      style={{
        ...fadeInAnimation,
        ...hoverAnimation
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      <div className="metric-content">
        <div className="metric-header">
          <div className="metric-title">{title}</div>
          {icon && <div className={`metric-icon ${hovered ? 'icon-animated' : ''}`}>{icon}</div>}
        </div>
        
        <div 
          className={`metric-value ${pulse ? 'pulse-text' : ''} ${isTotalHojeCard ? 'total-hoje-value' : ''} ${isLargeValue ? 'large-value' : ''}`} 
          title={typeof value === 'string' ? value : formattedValue()}
        >
          {typeof value === 'string' || !animated ? (
            formattedValue()
          ) : (
            <AnimatedCounter 
              value={value} 
              formatter={formatters[format]}
              duration={1200}
              animationType={isTotalHojeCard ? 'gentle' : 'easeOut'}
              highlightChange={hasBeenVisible}
            />
          )}
        </div>
        
        <div className="metric-details">
          {subtitle && (
            <div className="metric-subtitle text-sm text-gray-400">{subtitle}</div>
          )}

          {subValue !== undefined && (
            <div className="metric-subvalue">
              {new Intl.NumberFormat('pt-BR').format(subValue)}
            </div>
          )}

          {formattedChange && (
            <div className={`metric-change ${getChangeColor()} ${Math.abs(change || 0) > 20 ? 'pulse-text' : ''}`}>
              {change && change > 0 ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
                </svg>
              )}
              {formattedChange}
            </div>
          )}
        </div>

        {description && <div className="metric-description">{description}</div>}
      </div>
      
      {/* Background gradient effect */}
      <div className="card-background-gradient"></div>
    </a.div>
  );
}

export default MetricCard;
